import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Carousel from "react-bootstrap/Carousel";
import { getDetailEvent } from "../services/apiService";
import "./EventDetailPage.css";
import { vibesColors as colors } from "../theme/vibesColors";
import {
  getBottleServiceHeroImageUrl,
  isBottleServiceEvent,
  isApiSuccess,
  pickAddress,
  pickImageUrl,
  pickRating,
  pickTitle,
} from "../utils/clubDetailHelpers";
import {
  buildDayPriceRowsForEvent,
  formatMoney,
  getPriceForCurrentDay,
  googleMapsEmbedUrl,
  googleMapsSearchUrl,
  pickCoordinates,
} from "../utils/eventDetailHelpers";
import WebsiteLayout from "../layouts/WebsiteLayout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import BackNavButton from "../components/common/BackNavButton";

/** Resolves host / club id for `/club/:id` (same id as `user/hostprofile` hostId). */
function hostIdFromEvent(ev) {
  if (!ev || typeof ev !== "object") return "";
  const tryStr = (v) =>
    typeof v === "string" && v.trim() ? v.trim() : "";
  const tryObj = (o) =>
    o && typeof o === "object" ? String(o._id || o.id || "").trim() : "";

  let id =
    tryStr(ev.hostId) ||
    tryStr(ev.host_id) ||
    tryStr(ev.clubId) ||
    tryStr(ev.venueId);
  if (id) return id;

  id = tryObj(ev.hostId) || tryObj(ev.host) || tryObj(ev.userId) || tryObj(ev.user);
  if (id) return id;

  id = tryStr(ev.userId);
  return id;
}

function heroImages(ev) {
  if (isBottleServiceEvent(ev)) {
    return [getBottleServiceHeroImageUrl()];
  }
  const photos = ev?.photos;
  const urls = [];
  if (Array.isArray(photos)) {
    for (const p of photos) {
      const u =
        typeof p === "string"
          ? pickImageUrl({ image: p })
          : pickImageUrl({ photos: [p] });
      if (u) urls.push(u);
    }
  }
  const single = pickImageUrl(ev);
  if (single && !urls.length) urls.push(single);
  return urls.filter(Boolean);
}

export default function EventDetailPage() {
  const { eventId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hostFromUrl = (searchParams.get("host") || "").trim();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDetailEvent(eventId);
        if (!isApiSuccess(res.status) || !res.data) {
          setError(res.message || "Event not found");
          setEvent(null);
          return;
        }
        if (!cancelled) {
          setEvent(res.data);
          console.log(res.data);
          setImgIdx(0);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setEvent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const images = useMemo(() => (event ? heroImages(event) : []), [event]);

  const backTo = useMemo(() => {
    if (hostFromUrl) return `/club/${encodeURIComponent(hostFromUrl)}`;
    const hid = event ? hostIdFromEvent(event) : "";
    return hid ? `/club/${encodeURIComponent(hid)}` : "/";
  }, [event, hostFromUrl]);
  const showBackButton = !location.pathname.startsWith("/club/event/");
  const fromClubList = Boolean(location.state?.fromClubList);
  const bookingReturnPath = location.pathname.startsWith("/club/event/")
    ? `${location.pathname}${location.search}`
    : fromClubList
      ? `/event/${encodeURIComponent(eventId)}`
      : `${location.pathname}${location.search}`;

  const coords = useMemo(() => (event ? pickCoordinates(event) : null), [event]);
  const address = event ? pickAddress(event) : "";
  const mapSearchHref = coords
    ? googleMapsSearchUrl(coords.lat, coords.lng, address)
    : googleMapsSearchUrl(null, null, address);

  const priceRows = useMemo(() => {
    if (!event) return [];
    return buildDayPriceRowsForEvent(event);
  }, [event]);

  const entryFeeToday = useMemo(() => {
    if (!event) return null;
    return getPriceForCurrentDay(event.entryFee);
  }, [event]);

  const minG = event?.minGuest ?? event?.minGuests ?? event?.minimumGuests;
  const maxG = event?.maxGuest ?? event?.maxGuests ?? event?.maximumGuests;

  useEffect(() => {
    if (!event || !eventId) return;
    const rawType =
      event?.type ||
      event?.category ||
      event?.eventType ||
      event?.eventCategory ||
      "";
    const normalizedType = String(rawType).trim().toLowerCase();
    const isBottleServiceType =
      normalizedType === "bottleservice" ||
      normalizedType === "bottle service";
    if (isBottleServiceType) {
      const query = new URLSearchParams();
      if (hostFromUrl) query.set("host", hostFromUrl);
      query.set("fromEvent", "1");
      const qs = query.toString();
      navigate(`/bottle/${encodeURIComponent(eventId)}${qs ? `?${qs}` : ""}`, {
        replace: true,
      });
      return;
    }
    const shouldGoToBooking =
      normalizedType === "booth" ||
      normalizedType === "vip entry" ||
      normalizedType === "vipentry";
    if (!shouldGoToBooking) return;
    navigate(`/booking/${eventId}`, {
      replace: true,
      state: { fromDetailPath: `${location.pathname}${location.search}` },
    });
  }, [event, eventId, hostFromUrl, location.pathname, location.search, navigate]);

  if (!eventId) {
    return (
      <WebsiteLayout>
        <div className="container py-4">
          <p style={{ color: colors.red }}>Missing event id.</p>
        </div>
      </WebsiteLayout>
    );
  }

  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner label="Loading…" />
      </WebsiteLayout>
    );
  }

  if (error || !event) {
    return (
      <WebsiteLayout>
        <div className="container py-4">
          <ErrorMessage message={error || "Not found"} />
          <Link to="/" style={{ color: colors.violate }}>
            Home
          </Link>
        </div>
      </WebsiteLayout>
    );
  }

  const title = pickTitle(event);
  const typeLabel = event.type ? String(event.type) : "Event";
  const isTableType = String(event.type || "").toLowerCase() === "table";
  const rating = pickRating(event);
  const about = event.details || event.description || event.about || "";
  const organization =
    event.organizationName ||
    event.organisationName ||
    event.businessName ||
    event.companyName ||
    event.hostName ||
    "";
  const orgName =
    organization ||
    event?.userId?.fullName ||
    event?.hostId?.fullName ||
    event?.user?.fullName ||
    "Organization";
  const orgTeam =
    event?.organizationTeam ||
    event?.teamName ||
    event?.hostRole ||
    "Organization Team";
  const orgPhone =
    event?.phone ||
    event?.phoneNumber ||
    event?.userId?.phone ||
    event?.hostId?.phone ||
    "";
  const orgEmail =
    event?.email ||
    event?.userId?.email ||
    event?.hostId?.email ||
    "";
  const orgAvatar =
    event?.businessPicture ||
    event?.businessLogo ||
    event?.profilePicture ||
    event?.userId?.businessPicture ||
    event?.userId?.profilePicture ||
    event?.hostId?.businessPicture ||
    event?.hostId?.profilePicture ||
    pickImageUrl(event?.userId || event?.hostId || event) ||
    "";
  const rawFacilities =
    event?.facilities ||
    event?.amenities ||
    event?.features ||
    event?.facility ||
    [];
  const facilities = Array.isArray(rawFacilities)
    ? rawFacilities
        .map((v) => {
          if (typeof v === "string") return v.trim();
          if (v && typeof v === "object") {
            const named = v.name || v.title || v.label || "";
            return String(named).trim();
          }
          return String(v || "").trim();
        })
        .filter(Boolean)
    : typeof rawFacilities === "string"
      ? rawFacilities
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    console.log("facilities", facilities, rawFacilities);
  const startDateRaw = event?.startDate || event?.eventStartDate || event?.date;
  const startDateLabel = startDateRaw
    ? new Date(startDateRaw).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Date TBD";
  const startTimeLabel = event?.openingTime || event?.startTime || "";

  const embedUrl = coords ? googleMapsEmbedUrl(coords.lat, coords.lng) : "";
  const showPriceTable = priceRows.length > 0;
  const hasEntryFee =
    entryFeeToday != null && entryFeeToday !== "";
  const showEntryFeeLine = hasEntryFee && !showPriceTable;

  return (
    <WebsiteLayout>
      <div className="container-fluid px-0 event-detail-page">
        <div className="position-relative w-100 event-detail-hero-wrap">
          <div className="event-hero-media">
            <div className="event-hero-frame">
              {images.length > 1 ? (
                <Carousel
                  activeIndex={imgIdx}
                  onSelect={(index) => setImgIdx(index)}
                  className="event-hero-carousel hero-carousel-frost"
                  indicators
                  controls
                  interval={5000}
                  pause="hover"
                >
                  {images.map((url, i) => (
                    <Carousel.Item key={i}>
                      <div className="event-hero-slide">
                        <img
                          src={url}
                          alt=""
                          className="event-hero-img"
                          loading={i === 0 ? "eager" : "lazy"}
                          decoding="async"
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <div className="event-hero-slide">
                  {images[0] ? (
                    <img
                      src={images[0]}
                      alt=""
                      className="event-hero-img"
                      loading="eager"
                      decoding="async"
                    />
                  ) : null}
                </div>
              )}
              <div className="event-hero-bottom-gradient" aria-hidden />
              <div
                className="event-hero-overlay position-absolute top-0 start-0 end-0 d-flex justify-content-between align-items-start p-3 p-md-3"
                style={{ pointerEvents: "none" }}
              >
                {showBackButton ? (
                  <BackNavButton to={backTo} className="back-nav-btn--overlay" ariaLabel="Back to venue" />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="event-detail-body w-100 pb-5">
          <div className="mx-auto event-detail-inner">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap event-detail-meta">
              <span className="event-detail-type-pill">{typeLabel}</span>
              <span className="event-detail-rating">
                ★ {rating != null ? String(rating) : "0"}
              </span>
            </div>

            <h1 className="event-detail-title mt-3 mb-2">{title}</h1>

            <div className="event-detail-quick-info">
              <div className="event-detail-quick-item">
                <span className="event-detail-quick-icon" aria-hidden>
                  📍
                </span>
                <span>{address || "Address not available"}</span>
              </div>
              <div className="event-detail-quick-item">
                <span className="event-detail-quick-icon" aria-hidden>
                  ⏱
                </span>
                <span>
                  {startDateLabel}
                  {startTimeLabel ? ` • ${startTimeLabel}` : ""}
                </span>
              </div>
            </div>

            {about ? (
              <section className="event-detail-section">
                <h2
                  className="mb-2"
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: colors.white,
                  }}
                >
                  About
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: colors.lightBackground,
                    lineHeight: 1.65,
                    fontSize: "0.95rem",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {about}
                </p>
              </section>
            ) : (
              <p className="mt-3 mb-0" style={{ color: colors.textColor }}>
                No description.
              </p>
            )}

            <section className="event-detail-section">
              <h2
                className="mb-2"
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: colors.white,
                }}
              >
                Organizations
              </h2>
              <div className="event-org-card">
                <div className="event-org-main">
                  <div className="event-org-avatar-wrap">
                    {orgAvatar ? (
                      <img src={orgAvatar} alt="" className="event-org-avatar" />
                    ) : (
                      <span className="event-org-avatar-fallback" aria-hidden>
                        🏢
                      </span>
                    )}
                  </div>
                  <div className="event-org-text">
                    <h3 className="event-org-name">{orgName}</h3>
                    <p className="event-org-team">{orgTeam}</p>
                  </div>
                </div>
                {(orgPhone || orgEmail) ? (
                  <div className="event-org-contact">
                    {orgPhone ? (
                      <a href={`tel:${orgPhone}`} className="event-org-action" aria-label="Call organization">
                        📞
                      </a>
                    ) : null}
                    {orgEmail ? (
                      <a
                        href={`mailto:${orgEmail}`}
                        className="event-org-action"
                        aria-label="Email organization"
                      >
                        ✉
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </section>

            <section className="event-detail-section">
              <h2
                className="mb-2"
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: colors.white,
                }}
              >
                Facilities
              </h2>
              {facilities.length ? (
                <div className="event-facilities-wrap">
                  {facilities.map((item, idx) => (
                    <span key={`${item}-${idx}`} className="event-facility-chip">
                      {item}
                    </span>
                  ))}
                </div>
              ) : (
                <p
                  style={{
                    margin: 0,
                    color: colors.textColor,
                    lineHeight: 1.55,
                    fontSize: "0.95rem",
                  }}
                >
                  Facilities information not available.
                </p>
              )}
            </section>

            <section className="event-detail-section">
              <h2
                className="mb-3"
                style={{
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  color: colors.white,
                }}
              >
                Price (Day-wise)
              </h2>
              {showPriceTable ? (
                <div className="event-price-table-wrap rounded-3">
                  <table className="event-price-table w-100 mb-0">
                    <thead>
                      <tr>
                        <th scope="col">Day</th>
                        {isTableType ? (
                          <>
                            <th scope="col">Male</th>
                            <th scope="col">Female</th>
                          </>
                        ) : (
                          <th scope="col">Guests</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {priceRows.map((row, idx) => (
                        <tr key={`${row.dayLabel}-${idx}`}>
                          <td>{row.dayLabel}</td>
                          {isTableType ? (
                            <>
                              <td>
                                {row.male != null ? formatMoney(row.male) : "—"}
                              </td>
                              <td>
                                {row.female != null ? formatMoney(row.female) : "—"}
                              </td>
                            </>
                          ) : (
                            <td>
                              {row.male != null
                                ? formatMoney(row.male)
                                : row.female != null
                                  ? formatMoney(row.female)
                                  : "—"}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {showEntryFeeLine ? (
                <p
                  className={showPriceTable ? "mt-3 mb-0" : "mb-0"}
                  style={{ color: colors.lightBackground }}
                >
                  <span style={{ color: colors.violate }}>Entry fee: </span>
                  {formatMoney(entryFeeToday)}
                </p>
              ) : null}
              {!showPriceTable && !hasEntryFee ? (
                <p className="mb-0" style={{ color: colors.textColor }}>
                  Pricing on request.
                </p>
              ) : null}
            </section>

            {((minG != null && minG !== "") ||
              (maxG != null && maxG !== "")) ? (
              <section className="event-detail-section">
                <h2
                  className="mb-2"
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: colors.white,
                  }}
                >
                  Guests Capacity
                </h2>
                <ul
                  className="list-unstyled mb-0"
                  style={{ color: colors.textColor, fontSize: "0.95rem" }}
                >
                  {minG != null && minG !== "" ? (
                    <li>
                      <span style={{ color: colors.white }}>Minimum Guests: </span>
                      {String(minG)}
                    </li>
                  ) : null}
                  {maxG != null && maxG !== "" ? (
                    <li>
                      <span style={{ color: colors.white }}>Maximum Guests: </span>
                      {String(maxG)}
                    </li>
                  ) : null}
                </ul>
              </section>
            ) : null}

            {address ? (
              <section className="event-detail-section">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <h2
                    className="mb-0"
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: colors.white,
                    }}
                  >
                    Address
                  </h2>
                  {mapSearchHref ? (
                    <a
                      href={mapSearchHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: colors.violate,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        textDecoration: "none",
                      }}
                    >
                      View on Map
                    </a>
                  ) : null}
                </div>
                <p
                  className="mb-3"
                  style={{
                    color: colors.textColor,
                    fontSize: "0.95rem",
                    lineHeight: 1.45,
                  }}
                >
                  <span style={{ color: colors.violate }}>📍</span> {address}
                </p>
                {embedUrl ? (
                  <div
                    className="rounded-3 overflow-hidden w-100"
                    style={{
                      border: `1px solid ${colors.violate}44`,
                      minHeight: 280,
                    }}
                  >
                    <iframe
                      title="Venue location"
                      src={embedUrl}
                      className="w-100 border-0"
                      style={{ minHeight: 280, height: 360 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  </div>
                ) : null}
              </section>
            ) : null}

            <div className="event-detail-footer d-flex justify-content-end">
              <Link
                to={`/booking/${eventId}`}
                state={{ fromDetailPath: bookingReturnPath }}
                className="event-detail-next-btn"
              >
                Next
              </Link>
            </div>
          </div>
        </div>
      </div>
    </WebsiteLayout>
  );
}
